// Home › Profit (pages/ux-full components/marketing.js), each view drawn from its action's answer: a day's
// gross profit with what isn't final yet (reports.jobCosting), the jobs with their margins and the day by
// technician (reports.jobCosting beside each job's jobs.get), and the huddle's last days (gp.huddle). Gross
// profit is revenue less materials, purchases, receipts and paid labour with its burden; the company's
// target margin (the margin we aim for, estimates.warnMarginPct) marks the jobs and technicians under it. They draw no frame: the
// screen puts each in a section.
import type { CostedJob, CostRow, HuddleDaysProps, JobMarginsProps, ProfitDayProps } from '../schemas.ts';
import type { ViewProps } from '../host.ts';
import { money, plural } from '../format.ts';
import { Action, Facts, Figures, Label, Meter } from '../primitives.tsx';
import { useShown } from './AdsViews.tsx';

/** A margin as a whole percent; a dash with no revenue. */
export const marginText = (pct: number | null): string => (pct === null ? '—' : `${Math.round(pct)} %`);
const margin = (c: { revenueCents: number; grossProfitCents: number }) =>
  c.revenueCents ? (c.grossProfitCents / c.revenueCents) * 100 : null;
/** Minutes as hours to a tenth: "2.5 h". */
export const hoursText = (minutes: number): string => `${(minutes / 60).toFixed(1)} h`;
/** Under the company's target margin, when it sets one and there's revenue to have a margin. */
export const underTarget = (pct: number | null, target: number | null): boolean =>
  target !== null && pct !== null && pct < target;

/** Why a job's numbers may still move: purchases waiting for the office, hours no pay rate covers. */
export function notFinalWhy(r: CostRow): string[] {
  return [
    ...(r.pendingPurchasesCents > 0
      ? [`${money(r.pendingPurchasesCents)} in purchases waits for the office’s OK, not in its costs yet`]
      : []),
    ...r.noPayRate.map((n) => `no pay rate for ${n.name}: ${hoursText(n.minutes)} not in its labour`),
  ];
}
/** A job with revenue and nothing ever logged against it: no part, no purchase, no receipt. */
const bare = (r: CostRow) => r.revenueCents > 0 && !r.materialsLogged;

const SOURCE = {
  ledger: null,
  direct:
    'Read from the work itself (the invoices, the stock used, the approved purchases): the books don’t hold these days.',
  both: 'Partly from the books, partly from the work itself for the days the books don’t hold.',
} as const;

/** The share against the target: the meter with the target's mark, and the word when it's under. */
function Share({ pct, target, label }: { pct: number; target: number | null; label: string }) {
  const under = underTarget(pct, target);
  return (
    <p className="flw-share">
      <Meter
        value={Math.max(0, pct)}
        max={100}
        label={label}
        state={under ? 'low' : 'ok'}
        {...(target !== null ? { marks: [{ at: target, label: `The ${target} % target` }] } : {})}
      />
      <b>{marginText(pct)}</b>
      {target !== null && (
        <span className="flw-sub">
          {under ? <Label kind="wait">Under the {target} % target</Label> : `The target is ${target} %.`}
        </span>
      )}
    </p>
  );
}

/** reports.jobCosting for a day: revenue, its costs and paid labour, gross profit, and what to check */
export function ProfitDay({ data: p, host }: ViewProps<ProfitDayProps>) {
  const waiting = p.rows.filter((r) => notFinalWhy(r).length > 0);
  const unlogged = p.rows.filter(bare);
  const [waitingShown, moreWaiting] = useShown(waiting);
  const [unloggedShown, moreUnlogged] = useShown(unlogged);
  if (!p.rows.length)
    return (
      <p className="flw-empty" role="status">
        No job made revenue {p.label.toLowerCase()}.
      </p>
    );
  const m = margin(p);
  const untracked = p.rows.reduce((a, r) => a + r.untrackedMaterialsCents, 0);
  return (
    <article aria-label={`Gross profit, ${p.label}`}>
      <p className="flw-sub">
        {p.label}, {plural(p.rows.length, 'job')} with revenue
      </p>
      <div className="flw-ledger">
        <Facts
          rows={[
            ['Revenue, before tax', money(p.revenueCents)],
            ['Materials, purchases and receipts', money(-p.costOfSalesCents)],
            [
              p.labourMinutes ? `Paid labour, ${hoursText(p.labourMinutes)}` : 'Paid labour',
              money(-p.labourCents),
            ],
          ]}
        />
      </div>
      <p className="flw-total">
        <span>Gross profit</span>
        <b className={p.grossProfitCents < 0 ? 'flw-bad' : undefined}>{money(p.grossProfitCents)}</b>
      </p>
      {m !== null && <Share pct={m} target={p.targetMarginPct} label="Gross profit, as a share of revenue" />}
      <p className="flw-sub">
        Labour is each person’s pay for their paid hours on the jobs, and their commissions, with{' '}
        {p.labourBurdenPct} % on top for the employer’s costs.
      </p>
      {waiting.length > 0 && (
        <>
          <p className="flw-sub">
            <Label kind="wait">
              {waiting.length === 1
                ? '1 number isn’t final yet'
                : `${waiting.length} numbers aren’t final yet`}
            </Label>
          </p>
          <ul className="flw-rows">
            {waitingShown.map((r) => (
              <li key={r.jobId} className="flw-row">
                <span>
                  <Action onClick={() => host.open({ kind: 'job', id: r.jobId })}>{r.jobNumber}</Action>
                  {notFinalWhy(r).map((w) => (
                    <span key={w} className="flw-sub">
                      {w}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
          {moreWaiting}
        </>
      )}
      {unlogged.length > 0 && (
        <>
          <p className="flw-sub">
            <Label kind="wait">
              {unlogged.length === 1
                ? '1 job has no materials logged'
                : `${unlogged.length} jobs have no materials logged`}
            </Label>
          </p>
          <ul className="flw-rows">
            {unloggedShown.map((r) => (
              <li key={r.jobId} className="flw-row">
                <span>
                  <Action onClick={() => host.open({ kind: 'job', id: r.jobId })}>{r.jobNumber}</Action>
                  <span className="flw-sub">
                    No part, purchase or receipt on it: check nothing was used, or that it was logged
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {moreUnlogged}
        </>
      )}
      {(untracked > 0 || SOURCE[p.source]) && (
        <p className="flw-sub">
          {untracked > 0 ? `${money(untracked)} of the materials are at the cost they had when used. ` : ''}
          {SOURCE[p.source] ?? ''}
        </p>
      )}
    </article>
  );
}

/** Who did a job: its visits' technicians, the lead first, each once, never a cancelled visit's. */
export function techsOf(job: CostedJob): string[] {
  const names: string[] = [];
  for (const a of job.appointments.filter((x) => x.status !== 'cancelled')) {
    const lead = a.techs.find((t) => t.id === a.leadTechId);
    for (const t of lead ? [lead, ...a.techs.filter((x) => x !== lead)] : a.techs)
      if (!names.includes(t.name)) names.push(t.name);
  }
  return names;
}

/** A row's words: what may still move, a job with no materials logged, else final. */
function Numbers({ r }: { r: CostRow }) {
  const out = [
    ...(notFinalWhy(r).length ? [['Not final', 'wait'] as const] : []),
    ...(bare(r) ? [['No materials', 'wait'] as const] : []),
  ];
  if (!out.length) return <Label kind="muted">Final</Label>;
  return (
    <>
      {out.map(([w, k]) => (
        <span key={w} className="flw-sub">
          <Label kind={k}>{w}</Label>
        </span>
      ))}
    </>
  );
}

/** A share in a table, with the word when it's under the target. */
function ShareCell({ pct, target }: { pct: number | null; target: number | null }) {
  return (
    <td className="flw-num">
      {marginText(pct)}
      {underTarget(pct, target) && (
        <span className="flw-sub">
          <Label kind="wait">under target</Label>
        </span>
      )}
    </td>
  );
}

/** reports.jobCosting's rows beside each job's record: one row a job, the day's totals under them */
export function JobMargins({ data: p, host }: ViewProps<JobMarginsProps>) {
  const c = p.costing;
  const jobs = new Map(p.jobs.map((j) => [j.id, j]));
  const [rows, more] = useShown([...c.rows].sort((a, b) => b.grossProfitCents - a.grossProfitCents));
  if (!c.rows.length)
    return (
      <p className="flw-empty" role="status">
        No job made revenue {p.label.toLowerCase()}.
      </p>
    );
  return (
    <article aria-label={`Jobs, ${p.label}`}>
      {c.targetMarginPct !== null && (
        <p className="flw-sub">Jobs under the {c.targetMarginPct} % target margin are marked.</p>
      )}
      <div className="flw-table-wrap">
        <table className="flw-table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Technician</th>
              <th className="flw-num">Revenue</th>
              <th className="flw-num">Costs</th>
              <th className="flw-num">Labour</th>
              <th className="flw-num">Gross profit</th>
              <th className="flw-num">Share</th>
              <th>Numbers</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const j = jobs.get(r.jobId);
              return (
                <tr key={r.jobId}>
                  <th scope="row">
                    <Action onClick={() => host.open({ kind: 'job', id: r.jobId })}>{r.jobNumber}</Action>{' '}
                    {j?.customerName ?? ''}
                    {j && <span className="flw-sub">{j.summary}</span>}
                  </th>
                  <td>{j ? techsOf(j).join(', ') || '—' : ''}</td>
                  <td className="flw-num">{money(r.revenueCents)}</td>
                  <td className="flw-num">{money(r.costOfSalesCents)}</td>
                  <td className="flw-num">
                    {money(r.labourCents)}
                    {r.labourMinutes > 0 && <span className="flw-sub">{hoursText(r.labourMinutes)}</span>}
                  </td>
                  <td className={`flw-num${r.grossProfitCents < 0 ? ' flw-bad' : ''}`}>
                    {money(r.grossProfitCents)}
                  </td>
                  <ShareCell pct={r.marginPct} target={c.targetMarginPct} />
                  <td>
                    <Numbers r={r} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">All {c.rows.length}</th>
              <td />
              <td className="flw-num">{money(c.revenueCents)}</td>
              <td className="flw-num">{money(c.costOfSalesCents)}</td>
              <td className="flw-num">
                {money(c.labourCents)}
                {c.labourMinutes > 0 && <span className="flw-sub">{hoursText(c.labourMinutes)}</span>}
              </td>
              <td className="flw-num">{money(c.grossProfitCents)}</td>
              <ShareCell pct={margin(c)} target={c.targetMarginPct} />
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      {more}
    </article>
  );
}

export interface TechShare {
  name: string;
  jobs: number;
  revenueCents: number;
  grossProfitCents: number;
  /** Their paid minutes on the jobs, the same minutes as the jobs' labour. */
  minutes: number;
  /** Gross profit for each hour logged; null with no time logged. */
  perHourCents: number | null;
  /** Their gross profit as a share of their revenue; null with no revenue. */
  marginPct: number | null;
}

/**
 * Each technician's share of the jobs: a job's revenue and gross profit split by the paid time each
 * has on it in the costing (work and drive, with the office's corrections: the minutes its labour
 * counts, so everyone's hours add up to the jobs', QA-684), or evenly among its technicians when nobody
 * has any. Best per hour first; those with no time after.
 */
export function byTechnician(rows: readonly CostRow[], jobs: readonly CostedJob[]): TechShare[] {
  const byId = new Map(jobs.map((j) => [j.id, j]));
  const out = new Map<string, TechShare>();
  for (const c of rows) {
    const j = byId.get(c.jobId);
    if (!j) continue;
    const total = c.labourBy.reduce((s, t) => s + t.minutes, 0);
    const shares: [string, number, number][] =
      total > 0
        ? c.labourBy.map((t) => [t.name, t.minutes / total, t.minutes])
        : techsOf(j).map((n, _i, all) => [n, 1 / all.length, 0]);
    for (const [name, share, minutes] of shares) {
      const r = out.get(name) ?? {
        name,
        jobs: 0,
        revenueCents: 0,
        grossProfitCents: 0,
        minutes: 0,
        perHourCents: null,
        marginPct: null,
      };
      r.jobs++;
      r.revenueCents += Math.round(c.revenueCents * share);
      r.grossProfitCents += Math.round(c.grossProfitCents * share);
      r.minutes += minutes;
      out.set(name, r);
    }
  }
  for (const r of out.values()) {
    r.perHourCents = r.minutes ? Math.round((r.grossProfitCents / r.minutes) * 60) : null;
    r.marginPct = margin(r);
  }
  return [...out.values()].sort(
    (a, b) =>
      (b.perHourCents ?? -Infinity) - (a.perHourCents ?? -Infinity) ||
      b.grossProfitCents - a.grossProfitCents,
  );
}

/** The same jobs, technician by technician, by the time each logged */
export function ProfitByTech({ data: p }: ViewProps<JobMarginsProps>) {
  const list = byTechnician(p.costing.rows, p.jobs);
  const [rows, more] = useShown(list);
  const target = p.costing.targetMarginPct;
  if (!list.length)
    return (
      <p className="flw-empty" role="status">
        Nobody is on these jobs.
      </p>
    );
  const most = Math.max(1, ...list.map((r) => r.perHourCents ?? 0));
  return (
    <article aria-label={`By technician, ${p.label}`}>
      <p className="flw-sub">
        Each job shared by the paid time each technician has on it, the hours its labour counts
        {target !== null ? `; those under the ${target} % target margin are marked` : ''}.
      </p>
      <div className="flw-table-wrap">
        <table className="flw-table">
          <thead>
            <tr>
              <th>Technician</th>
              <th className="flw-num">Jobs</th>
              <th className="flw-num">Gross profit</th>
              <th className="flw-num">Hours</th>
              <th>For each hour</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.name}>
                <th scope="row">{t.name}</th>
                <td className="flw-num">{t.jobs}</td>
                <td className={`flw-num${t.grossProfitCents < 0 ? ' flw-bad' : ''}`}>
                  {money(t.grossProfitCents)}
                  {t.marginPct !== null && (
                    <span className="flw-sub">
                      {marginText(t.marginPct)} of revenue
                      {underTarget(t.marginPct, target) && (
                        <>
                          {' '}
                          <Label kind="wait">under target</Label>
                        </>
                      )}
                    </span>
                  )}
                </td>
                <td className="flw-num">{t.minutes ? hoursText(t.minutes) : '—'}</td>
                <td>
                  {t.perHourCents === null ? (
                    <span className="flw-muted">no time logged</span>
                  ) : (
                    <span className="flw-per">
                      <Meter
                        value={Math.max(0, t.perHourCents)}
                        max={most}
                        label={`${t.name} for each hour`}
                      />
                      <b>{money(t.perHourCents)}</b>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {more}
    </article>
  );
}

const weekday = (date: string) =>
  new Intl.DateTimeFormat('en-CA', { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(`${date}T12:00:00Z`),
  );
const dayNum = (date: string) => `${Number(date.slice(8, 10))}`;

/** gp.huddle: the targets the office plans by, and the last working days, newest first */
export function HuddleDays({
  data: p,
  host,
  last = 5,
}: ViewProps<HuddleDaysProps> & { last?: number | null }) {
  const gone = p.days.filter((d) => !d.future && (d.working || d.revenueCents)).reverse();
  const shown = last ? gone.slice(0, last) : gone;
  const most = Math.max(1, ...shown.map((d) => d.revenueCents));
  return (
    <article aria-label="The morning huddle">
      <Figures
        items={[
          {
            label: 'Technicians planned',
            value: p.targets.plannedTechs === null ? '—' : String(p.targets.plannedTechs),
          },
          {
            label: 'Calls a technician a day',
            value: p.targets.callsPerTechDay === null ? '—' : String(p.targets.callsPerTechDay),
          },
          {
            label: 'Conversion rate',
            value: p.budgeted.conversionPct === null ? '—' : `${p.budgeted.conversionPct} %`,
          },
          {
            label: 'Average ticket',
            value: p.budgeted.avgTicketCents === null ? '—' : money(p.budgeted.avgTicketCents),
          },
        ]}
      />
      {!shown.length ? (
        <p className="flw-empty" role="status">
          No working day has passed this month yet.
        </p>
      ) : (
        <ul className="flw-rows">
          {shown.map((d) => (
            <li key={d.date} className="flw-bar">
              <span>
                <b>{d.today ? 'Today' : weekday(d.date)}</b> {dayNum(d.date)}
              </span>
              <Meter
                value={d.revenueCents}
                max={most}
                label={`Revenue earned ${d.date}`}
                fill={d.closed ? 'hatch' : 'solid'}
              />
              <b>{money(d.revenueCents)}</b>
              <span className="flw-sub">
                {d.closed ? `${d.closed}. ` : ''}
                {plural(d.jobs, 'job')}, {d.sold} sold
                {d.provisionalCents ? `, ${money(d.provisionalCents)} on draft invoices` : ''}
                {d.note ? `. ${d.note}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {last !== null && gone.length > shown.length && (
        <p className="flw-more">
          <Action onClick={() => host.open({ kind: 'numbers' })}>Every day of the month</Action>
        </p>
      )}
    </article>
  );
}
